import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  AUDIENCE_WIZARD_BUTTON_CLASS,
  AUDIENCE_WIZARD_BUTTON_VARIANT,
} from '@/lib/constants/audience-wizard'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { displaySegmentValueLabel } from '@/lib/utils/column-labels'
import { SegmentFilterQuestionPicker } from '@/components/audiences/SegmentFilterQuestionPicker'

import type { SegmentFilterQuestion } from '@/lib/utils/segment-filter-questions'

interface AddFilterModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Addable questions (single columns and grouped questions) from the catalog. */
  questions: Array<SegmentFilterQuestion>
  /** Add the selected question/group as a single logical filter. */
  onAdd: (question: SegmentFilterQuestion, customLabel?: string) => void
  /** Override the modal title (default: "Add a segment filter"). */
  title?: string
  /** Override the add button label (default: "Add filter"). */
  addButtonLabel?: string
}

export function AddFilterModal({
  open,
  onOpenChange,
  questions,
  onAdd,
  title: titleOverride,
  addButtonLabel,
}: AddFilterModalProps) {
  const { t } = useTranslation('app')
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null)
  const [filterName, setFilterName] = useState('')

  const pickedQuestion = useMemo(
    () => questions.find((q) => q.id === selectedQuestionId) ?? null,
    [questions, selectedQuestionId],
  )
  const isGroup = pickedQuestion?.kind === 'group'

  useEffect(() => {
    if (!open) {
      setSelectedQuestionId(null)
      setFilterName('')
    }
  }, [open])

  function handlePickQuestion(item: SegmentFilterQuestion) {
    setSelectedQuestionId(item.id)
    setFilterName('')
  }

  function handleAdd() {
    if (!pickedQuestion) return
    onAdd(pickedQuestion, filterName.trim() || undefined)
    onOpenChange(false)
  }

  const singleOption = !isGroup ? pickedQuestion?.options[0] ?? null : null

  // Deduplicated union of every option's displayed values (order-preserving),
  // so the grouped view shows a single "set" of answers instead of repeating
  // the same value across every option row.
  const groupUniqueValues = useMemo(() => {
    if (!pickedQuestion || !isGroup) return []
    const seen = new Set<string>()
    const out: Array<string> = []
    for (const opt of pickedQuestion.options) {
      for (const raw of opt.sampleValues) {
        const label = displaySegmentValueLabel(
          { valueLabels: opt.valueLabels, valueMap: opt.valueMap },
          raw,
        )
        if (!seen.has(label)) {
          seen.add(label)
          out.push(label)
        }
      }
    }
    return out.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
  }, [pickedQuestion, isGroup])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl gap-0 p-0 sm:p-0 overflow-hidden flex flex-col max-h-[calc(85vh-20px)]">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="font-display text-xl font-normal">
            {titleOverride ?? t('audienceCreate.addSegmentFilter')}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {t('audienceCreate.filterModalHelper')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-5 px-6 pb-4 overflow-y-auto flex-1 min-h-0">
          {/* Filter name — always shown first */}
          <div className="flex flex-col gap-1.5 min-w-0">
            <Label htmlFor="add-filter-name">{t('audienceCreate.filterName')}</Label>
            <Input
              id="add-filter-name"
              className="h-9 text-sm"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              placeholder={pickedQuestion ? pickedQuestion.label : t('audienceCreate.enterFilterName')}
            />
          </div>

          {/* Question / group picker */}
          <div className="flex flex-col gap-1.5 min-w-0">
            <Label>{t('audienceCreate.question')}</Label>
            <SegmentFilterQuestionPicker
              questions={questions}
              value={{
                sourceColumn: pickedQuestion?.kind === 'single'
                  ? pickedQuestion.options[0]?.sourceColumn
                  : undefined,
                groupId: pickedQuestion?.kind === 'group'
                  ? pickedQuestion.id
                  : undefined,
              }}
              onSelectGroup={handlePickQuestion}
              onSelectColumn={(_col, parentQuestion) => handlePickQuestion(parentQuestion)}
              placeholder={t('audienceCreate.chooseQuestion')}
              searchPlaceholder={t('audienceCreate.searchQuestions')}
            />
          </div>

          {/* Single column — original + sample values */}
          {pickedQuestion && singleOption ? (
            <>
              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <p className="line-clamp-2 break-all text-sm text-muted-foreground cursor-default">
                      {t('audienceCreate.originalLabel', { label: singleOption.originalLabel })}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" align="start" className="max-w-sm text-xs">
                    {singleOption.originalLabel}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {singleOption.sampleValues.length > 0 ? (
                <div className="flex flex-col gap-1.5">
                  <Label>{t('audienceCreate.sampleValues')}</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {singleOption.sampleValues.map((v) => (
                      <Badge key={v} variant="outline" className="text-sm font-normal border-0 bg-primary/20">
                        {displaySegmentValueLabel(
                          {
                            valueLabels: singleOption.valueLabels,
                            valueMap: singleOption.valueMap,
                          },
                          v,
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}

          {/* Grouped question — options + values */}
          {pickedQuestion && isGroup ? (
            <>
              <TooltipProvider delayDuration={300}>
                <div className="flex min-w-0 flex-col gap-3">
                  {/* Options row — one item per option */}
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <Label>{t('audienceCreate.options')}</Label>
                    <ScrollArea className="h-[100px] rounded-md border border-border/40">
                      <p className="p-2 text-[15px] leading-relaxed">
                        {pickedQuestion.options.map((opt, i) => (
                          <span key={opt.sourceColumn}>
                            {opt.optionLabel}
                            {i < pickedQuestion.options.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </p>
                    </ScrollArea>
                  </div>

                  {/* Values row — deduped set of all answers */}
                  <div className="flex min-w-0 flex-col gap-1.5">
                    <Label>{t('audienceCreate.values')}</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {groupUniqueValues.length > 0 ? (
                        groupUniqueValues.map((value) => (
                          <Tooltip key={value}>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className="max-w-full cursor-default text-sm font-normal border-0 bg-primary/20"
                              >
                                <span className="min-w-0 truncate">{value}</span>
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent
                              side="top"
                              align="start"
                              className="max-w-sm text-xs"
                            >
                              {value}
                            </TooltipContent>
                          </Tooltip>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">{t('audienceCreate.noSampleValues')}</p>
                      )}
                    </div>
                  </div>
                </div>
              </TooltipProvider>
            </>
          ) : null}
        </div>

        <DialogFooter className="px-6 pb-6 pt-2 sm:justify-center">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('audienceCreate.cancel')}
          </Button>
          <Button
            variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
            className={AUDIENCE_WIZARD_BUTTON_CLASS}
            disabled={!pickedQuestion}
            onClick={handleAdd}
          >
            {addButtonLabel ?? t('audienceCreate.addFilter')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
